import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useCurrency } from "../lib/currency";

/**
 * Compact currency picker for the site header (desktop + mobile).
 * Shows flag + code, opens a small menu of supported currencies + a
 * "Show INR reference" toggle.
 */
export default function CurrencySelector({ compact = false, testId = "currency-selector" }) {
  const { currency, setCurrency, showInrRef, setShowInrRef, meta, supported } = useCurrency();
  const [open, setOpen] = useState(false);
  const m = meta[currency] || {};

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testId}-trigger`}
        className={`inline-flex items-center gap-1 sm:gap-1.5 rounded-sm border border-border bg-card hover:bg-muted/50 transition-colors text-foreground h-10 sm:h-auto ${
          compact ? "px-2 py-1 text-[11px]" : "px-2 sm:px-2.5 sm:py-1.5 text-[11px] sm:text-xs"
        } font-mono font-bold uppercase tracking-widest`}
      >
        <span className="text-base leading-none">{m.flag || "🌐"}</span>
        <span>{currency}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close currency menu"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            data-testid={`${testId}-menu`}
            className="absolute right-0 mt-2 w-64 z-50 rounded-md border border-border bg-card shadow-lg overflow-hidden"
          >
            <div className="px-3 py-2 bg-muted/40 border-b border-border">
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground font-bold">Region &amp; Currency</div>
              <p className="mt-0.5 text-[10px] text-muted-foreground">Prices convert from INR at daily FX rates.</p>
            </div>
            <ul className="py-1 max-h-72 overflow-y-auto">
              {supported.map((code) => {
                const cm = meta[code] || {};
                const selected = code === currency;
                return (
                  <li key={code}>
                    <button
                      type="button"
                      onClick={() => { setCurrency(code); setOpen(false); }}
                      data-testid={`${testId}-option-${code.toLowerCase()}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                        selected ? "bg-brand-blue/10 text-brand-blue font-semibold" : "text-foreground"
                      }`}
                    >
                      <span className="text-lg leading-none">{cm.flag || "🌐"}</span>
                      <span className="font-mono font-bold text-xs">{code}</span>
                      <span className="text-xs text-muted-foreground truncate">{cm.name}</span>
                      {selected && <Check className="h-3.5 w-3.5 ml-auto shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
            {currency !== "INR" && (
              <div className="border-t border-border px-3 py-2 flex items-center gap-2">
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={showInrRef}
                    onChange={(e) => setShowInrRef(e.target.checked)}
                    data-testid={`${testId}-inr-toggle`}
                    className="h-3.5 w-3.5 accent-brand-blue"
                  />
                  <span className="text-[11px] font-mono text-foreground">Show INR reference</span>
                </label>
                <span className="text-[10px] font-mono text-muted-foreground">₹</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
